import { useState, useEffect } from 'react';
import { Menu, X, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { SiFacebook } from 'react-icons/si';
import { CONTACT_INFO } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#inicio', label: 'Início' },
    { href: '#servicos', label: 'Serviços' },
    { href: '#sobre', label: 'Sobre' },
    { href: '#contacto', label: 'Contacto' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background/80 backdrop-blur-md border-b border-border shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <a href="#inicio" className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-2xl">LS</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg text-foreground leading-tight">
                  {CONTACT_INFO.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  Técnico de Contabilidade
                </span>
              </div>
            </a>

            <nav className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-foreground/80 hover:text-primary transition-colors duration-200 font-medium"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-foreground hover:text-primary transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-background border-t border-border">
            <nav className="container mx-auto px-4 py-4 flex flex-col space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-foreground/80 hover:text-primary transition-colors duration-200 font-medium py-2"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 pt-16">{children}</main>

      <footer className="bg-card border-t border-border mt-24">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-semibold text-lg mb-4 text-foreground">
                {CONTACT_INFO.name}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                Técnico Especialista em Contabilidade e Fiscalidade. Atendimento remoto em todo o país.
              </p>
              <a
                href="https://www.facebook.com/profile.php?id=61571716832838"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors text-sm font-medium"
              >
                <SiFacebook size={18} />
                <span>{CONTACT_INFO.facebook}</span>
              </a>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4 text-foreground">Contacto</h3>
              <div className="space-y-3">
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  <Mail size={16} />
                  <span>{CONTACT_INFO.email}</span>
                </a>
                <a
                  href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}
                  className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  <Phone size={16} />
                  <span>{CONTACT_INFO.phone}</span>
                </a>
                <div className="flex items-start space-x-2 text-muted-foreground text-sm">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                  <span>{CONTACT_INFO.address}</span>
                </div>
                <a
                  href="https://www.facebook.com/profile.php?id=61571716832838"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  <SiFacebook size={16} />
                  <span>Facebook</span>
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4 text-foreground">Horário</h3>
              <div className="flex items-start space-x-2 text-muted-foreground text-sm">
                <Clock size={16} className="mt-0.5 flex-shrink-0" />
                <span>{CONTACT_INFO.schedule}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center">
            <p className="text-muted-foreground text-sm">
              © 2026 {CONTACT_INFO.name}. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
